import React from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Button,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import CancelIcon from "@mui/icons-material/Cancel";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import { motion } from "framer-motion";
import PsychologyIcon from "@mui/icons-material/Psychology";
import TimelineIcon from "@mui/icons-material/Timeline";
import AutoAwesomeMotionIcon from "@mui/icons-material/AutoAwesomeMotion";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar/Navbar";
import Footer from "../components/Footer/Footer";

const themeColor = "#286a94";

const Solutions = () => {
  const navigate = useNavigate();

  return (
    <>
      <Navbar />

      {/* ================= Hero Section ================= */}
      <Box
        sx={{
          width: "100%",
          py: { xs: 6, md: 10 },
          px: 2,
          background: `linear-gradient(135deg, #1c4561, #286a94)`,
          color: "white",
          textAlign: "center",
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            mb: 2,
            fontSize: { xs: "28px", md: "40px" },
            letterSpacing: "0.5px",
          }}
        >
          Hire Smarter with eBench.ai
        </Typography>

        <Typography
          variant="body1"
          sx={{
            maxWidth: "750px",
            mx: "auto",
            opacity: 0.9,
            fontSize: { xs: "14px", md: "17px" },
            lineHeight: 1.7,
          }}
        >
          AI-powered hiring platform designed to match talent with roles based
          on real skills, work ethics, and performance — not just keywords.
        </Typography>

        <Box
          sx={{
            mt: 4,
            display: "flex",
            gap: 2,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="contained"
            onClick={() => navigate("/register")}
            sx={{
              px: 4,
              py: 1.3,
              borderRadius: "10px",
              fontWeight: 600,
              background: "#ffffff",
              color: themeColor,
              "&:hover": { background: "#f3f7fa" },
            }}
            startIcon={<RocketLaunchIcon />}
          >
            Get Started
          </Button>

          <Button
            variant="outlined"
            onClick={() => navigate("/register")}
            startIcon={<WorkOutlineIcon />}
            sx={{
              borderColor: "white",
              color: "white",
              px: 4,
              py: 1.3,
              borderRadius: "10px",
              fontWeight: 600,
              "&:hover": { background: "#ffffff22" },
            }}
          >
            Explore the platform
          </Button>
        </Box>
      </Box>

      {/* ================= Main Content ================= */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography
          variant="h4"
          sx={{
            textAlign: "center",
            fontWeight: 700,
            color: themeColor,
            mb: 2,
          }}
        >
          Our Solutions
        </Typography>

        <Typography
          variant="body1"
          sx={{
            textAlign: "center",
            maxWidth: "700px",
            mx: "auto",
            color: "#525252",
            mb: 5,
            lineHeight: 1.7,
          }}
        >
          eBench.ai automates repetitive hiring steps, improves candidate
          quality scoring, and helps companies build faster, bias-free, and
          skills-focused recruitment pipelines.
        </Typography>

        <Grid container spacing={4}>
          {[
            {
              icon: <PsychologyIcon sx={{ fontSize: 45, color: themeColor }} />,
              title: "AI-Based Job Matching",
              desc: "Machine learning maps candidate strengths with role requirements, improving relevance and reducing manual screening.",
            },
            {
              icon: <TimelineIcon sx={{ fontSize: 45, color: themeColor }} />,
              title: "Skill Intelligence & Scoring",
              desc: "Evaluate candidates based on verified skills, certifications, and real experience—not just job titles.",
            },
            {
              icon: (
                <AutoAwesomeMotionIcon
                  sx={{ fontSize: 45, color: themeColor }}
                />
              ),
              title: "Automated Hiring Workflow",
              desc: "From job posting to interviews—automate repetitive work, improve efficiency, and accelerate hiring.",
            },
            {
              icon: <TrendingUpIcon sx={{ fontSize: 45, color: themeColor }} />,
              title: "Candidate Growth Insights",
              desc: "Candidates receive personalized learning guidance, recommended job matches, and growth suggestions.",
            },
          ].map((item, idx) => (
            <Grid item xs={12} md={6} key={idx}>
              <Paper
                elevation={4}
                sx={{
                  p: 4,
                  borderRadius: "16px",
                  textAlign: "center",
                  borderTop: `6px solid ${themeColor}`,
                  transition: "0.3s",
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: "0px 10px 25px rgba(0,0,0,0.15)",
                  },
                }}
              >
                {item.icon}
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, mt: 2, mb: 1 }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#616161", lineHeight: 1.6 }}
                >
                  {item.desc}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

     {/* ================= Comparison Table ================= */}
<Box sx={{ mt: 10 }}>
  <Typography
    variant="h4"
    sx={{ fontWeight: 700, textAlign: "center", color: themeColor, mb: 1 }}
  >
    eBench.ai vs Traditional Hiring Platforms
  </Typography>

  <Typography
    variant="body2"
    sx={{ textAlign: "center", color: "#555", mb: 4 }}
  >
    See how eBench.ai evolves recruitment beyond outdated ATS systems.
  </Typography>

  <Paper
    sx={{
      p: { xs: 2, md: 4 },
      borderRadius: "16px",
      overflowX: "auto",
      boxShadow: "0px 5px 20px rgba(0,0,0,0.08)",
    }}
  >
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        minWidth: "600px",
      }}
    >
      <thead>
        <tr style={{ background: "#e8f4fb" }}>
          <th style={{ padding: "14px", textAlign: "left", color: themeColor, fontWeight: 700 }}>
            Feature
          </th>
          <th style={{ padding: "14px", textAlign: "center", fontWeight: 600 }}>
            Traditional ATS
          </th>
          <th style={{ padding: "14px", textAlign: "center", fontWeight: 700, color: themeColor, background: "#dff3ff" }}>
            eBench.ai ⭐
          </th>
        </tr>
      </thead>

      <tbody>
        {[
          {
            label: "Matching Logic",
            old: <CancelIcon sx={{ color: "#d9534f", fontSize: 28 }} />,
            new: <CheckCircleIcon sx={{ color: "#25c059", fontSize: 28 }} />,
          },
          {
            label: "Skill Verified Scoring",
            old: <CancelIcon sx={{ color: "#d9534f", fontSize: 28 }} />,
            new: <CheckCircleIcon sx={{ color: "#25c059", fontSize: 28 }} />,
          },
          {
            label: "Bias-Free Ranking",
            old: <CancelIcon sx={{ color: "#d9534f", fontSize: 28 }} />,
            new: <CheckCircleIcon sx={{ color: "#25c059", fontSize: 28 }} />,
          },
          {
            label: "Automation Level",
            old: "Low / Manual",
            new: <motion.span animate={{ scale: [1, 1.1, 1] }} style={{ color: themeColor, fontWeight: 700 }}>
              Fully Automated ⚡
            </motion.span>,
          },
          {
            label: "Candidate Experience",
            old: "Generic form-based flow",
            new: "Personalized journey + job recommendations 🎯",
          },
          {
            label: "Time to Shortlist",
            old: "Days / Weeks",
            new: "Minutes ⏱️",
          },
        ].map((row, i) => (
          <tr key={i} style={{ borderBottom: "1px solid #e5e5e5" }}>
            <td style={{ padding: "14px", fontWeight: 600 }}>{row.label}</td>
            <td style={{ padding: "14px", textAlign: "center" }}>{row.old}</td>
            <td style={{ padding: "14px", textAlign: "center", fontWeight: 600 }}>{row.new}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </Paper>

  {/* CTA */}
  <Box
    sx={{
      textAlign: "center",
      mt: 4,
      display: "flex",
      justifyContent: "center",
    }}
  >
    <motion.div
      initial={{ scale: 0.9 }}
      animate={{ scale: [1, 1.05, 1] }}
      transition={{ repeat: Infinity, duration: 2 }}
    >
      <Button
        variant="contained"
        sx={{
          px: 5,
          py: 1.4,
          borderRadius: "12px",
          fontWeight: 700,
          background: themeColor,
          "&:hover": { background: "#1f5679" },
          fontSize: "16px",
        }}
        onClick={() => navigate("/register")}
        endIcon={<TrendingFlatIcon />}
      >
        Try It Now
      </Button>
    </motion.div>
  </Box>
</Box>



        {/* ================= Value Tag ================= */}
        <Box sx={{ textAlign: "center", mt: 6 }}>
          <CheckCircleIcon sx={{ fontSize: 40, color: themeColor, mb: 1 }} />
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: themeColor }}
          >
            Smarter Hiring. Fair Matching. Better Careers.
          </Typography>
        </Box>

        {/* ================= CTA Section ================= */}
        <Box
          sx={{
            mt: 6,
            textAlign: "center",
            p: 4,
            borderRadius: "16px",
            background: "#f4faff",
            border: `1px solid ${themeColor}22`,
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: themeColor, mb: 2 }}
          >
            Be Part of the Future of Talent Matching
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexWrap: "wrap",
              mt: 2,
            }}
          >
            <Button
              variant="contained"
              sx={{
                background: themeColor,
                px: 4,
                py: 1.3,
                borderRadius: "10px",
                fontWeight: 600,
                "&:hover": { background: "#1d506c" },
              }}
              startIcon={<BusinessCenterIcon />}
              onClick={() => navigate("/demo")}
            >
              Request Demo
            </Button>

            <Button
              variant="outlined"
              startIcon={<RocketLaunchIcon />}
              sx={{
                borderColor: themeColor,
                color: themeColor,
                px: 4,
                py: 1.3,
                borderRadius: "10px",
                fontWeight: 600,
                "&:hover": { background: "#e3f2fa" },
              }}
              onClick={() => navigate("/register")}
            >
              Start Using eBench.ai
            </Button>
          </Box>
        </Box>
      </Container>
      <Footer/>
    </>
  );
};

export default Solutions;
